/**
 * Double-fire guard for admin document-review decisions.
 *
 * Owner report: in the AI Review Result popup an admin clicked Reject
 * twice and the decision WROTE TWICE — two status writes, two reviewLog
 * entries, two audit logs, two applicant emails. The decision buttons
 * stayed live for the whole request round-trip, local state updated only
 * after the write, and nothing anywhere deduped a repeat.
 *
 * This module is the single decision path every ApplicantDetail surface
 * (AI popup, rejection modal, row buttons, bulk apply) funnels into via
 * `handleReviewDocument` in useApplications.ts. It guards in two layers:
 *
 *  1. IN-FLIGHT: a synchronous Set check+add at function entry (before
 *     any await) makes a second invocation for the same document return
 *     'in-flight' immediately — a double-click cannot start a second
 *     write, regardless of how slow the first one is.
 *  2. RECORDED: before writing, the document is re-read FRESH from the
 *     store and the intended decision is compared with the decision
 *     already recorded (isRecordedDocumentDecision). A replay — second
 *     click that slipped past a remount, a second tab, a retried
 *     submission — returns 'deduplicated' as a no-op success: no second
 *     write, no second reviewLog entry, no second notification.
 *
 * A genuinely NEW decision is never suppressed: a different target
 * status, a different rejection note, a different deciding admin, or a
 * fresh AI provenance (new model verdict) all write normally, and the
 * same decision re-arms after any intervening state change because the
 * comparison runs against the document's LATEST review record.
 *
 * The server twin, isDuplicateDocumentStatusSubmission, guards
 * POST /api/admin/documents/update-status (the admin-upload validation
 * apply path) against replayed decision payloads.
 */

import { applicantVerdictForHumanStatus } from './applicantDocumentStatus';

export type DocumentDecisionAction = 'Approved' | 'Rejected';
export type DocumentDecisionOutcome =
  | 'applied'
  | 'deduplicated'
  | 'in-flight'
  | 'not-found'
  | 'failed';

export interface DocumentDecisionIntent {
  action: DocumentDecisionAction;
  /** Rejection note ('' for approvals and note-less rejections). */
  note?: string;
  /** The deciding admin's uid — must match the recorded reviewLog `by`. */
  by: string;
  /** AI provenance when the decision applies an AI verdict, else null. */
  aiProvenance?: Record<string, any> | null;
}

/**
 * Is this exact decision already the document's recorded decision?
 *
 * The recorded decision is the document's CURRENT status plus its LATEST
 * reviewLog entry. All of these must agree: target status, log action,
 * deciding admin, note, and AI identity (a human intent never matches an
 * AI-applied record and vice versa; an AI intent matches only the same
 * model verdict — model + decision + reason + source).
 */
export function isRecordedDocumentDecision(
  doc: Record<string, any> | null | undefined,
  intent: DocumentDecisionIntent
): boolean {
  if (!doc || doc.status !== intent.action) return false;
  const logs: any[] = Array.isArray(doc.reviewLogs) ? doc.reviewLogs : [];
  const last = logs[logs.length - 1];
  if (!last || last.action !== intent.action) return false;
  if (last.by !== intent.by) return false;
  if (String(last.note || '') !== String(intent.note || '')) return false;

  if (intent.aiProvenance) {
    // AI-applied decision: the record must be the SAME AI verdict —
    // AI-assisted log entry + the document's aiReview block carrying the
    // same model, decision, reason and source.
    if (!last.aiAssisted) return false;
    const stored = doc.aiReview;
    if (!stored || typeof stored !== 'object') return false;
    const prov = intent.aiProvenance;
    return (
      String(stored.model || '') === String(prov.model || '') &&
      String(stored.decision || '') === String(prov.decision || '') &&
      String(stored.reason || '') === String(prov.reason || '') &&
      String(stored.source || '') === String(prov.source || '')
    );
  }

  // Human decision: the recorded one must not be an AI-applied decision
  // (its log entry is AI-assisted and/or the document still carries the
  // aiReview provenance block a human decision would have cleared).
  if (last.aiAssisted) return false;
  if (doc.aiReview) return false;
  return true;
}

export interface DocumentDecisionDeps {
  /** Fresh read of the document record (null when it does not exist). */
  getDocument: (applicationId: string, docId: string) => Promise<Record<string, any> | null>;
  /** The single status write. */
  updateDocument: (applicationId: string, docId: string, data: Record<string, any>) => Promise<void>;
  /** Audit trail entry (best-effort — failures must not fail the decision). */
  writeAuditLog: (action: string, applicationId: string, message: string) => Promise<void>;
  /** Applicant notification for a REAL decision (email trigger). */
  notifyDecision: (args: { action: DocumentDecisionAction; docName: string; note: string }) => Promise<void>;
  /** Shared in-flight registry (one per hook instance). */
  inFlight: Set<string>;
  /** Id generator for reviewLog entries (defaults to crypto.randomUUID). */
  newId?: () => string;
  /** UI mirror so surfaces can disable their buttons while in flight. */
  onInFlightChange?: (docId: string, inFlight: boolean) => void;
}

export interface DocumentDecisionArgs {
  applicationId: string;
  docId: string;
  action: DocumentDecisionAction;
  note?: string;
  aiProvenance?: Record<string, any> | null;
  adminUid: string;
  adminName: string;
  /** ISO timestamp stamped on the decision. */
  now: string;
}

export interface DocumentDecisionResult {
  outcome: DocumentDecisionOutcome;
  /** The write performed (outcome 'applied'). */
  updateData?: Record<string, any>;
  /** The document as recorded (fresh read, merged with the write when applied). */
  doc?: Record<string, any> | null;
}

/**
 * Apply one admin document decision under the double-fire guard.
 *
 * The in-flight check+add runs synchronously at entry — before the first
 * await — so a rapid second invocation is refused even while the first
 * is still reading. A failed write returns 'failed' and does NOT audit
 * or notify (previously the email fired even when the Firestore write
 * had been blocked; a notification must follow a recorded decision).
 */
export async function applyDocumentDecision(
  deps: DocumentDecisionDeps,
  args: DocumentDecisionArgs
): Promise<DocumentDecisionResult> {
  const { applicationId, docId, action } = args;
  const note = args.note || '';
  const flightKey = `${applicationId}:${docId}`;

  if (deps.inFlight.has(flightKey)) {
    return { outcome: 'in-flight' };
  }
  deps.inFlight.add(flightKey);
  deps.onInFlightChange?.(docId, true);

  try {
    const fresh = await deps.getDocument(applicationId, docId);
    if (!fresh) return { outcome: 'not-found', doc: null };

    if (
      isRecordedDocumentDecision(fresh, {
        action,
        note: action === 'Rejected' ? note : '',
        by: args.adminUid,
        aiProvenance: args.aiProvenance || null,
      })
    ) {
      // Already recorded — no-op success. The caller syncs its local
      // state from `doc` so the admin sees the recorded decision.
      return { outcome: 'deduplicated', doc: fresh };
    }

    // The write shape is the historical handleReviewDocument payload,
    // with one correction: the reviewLogs base is the FRESH record, not
    // a possibly stale UI snapshot, so concurrent surfaces can no
    // longer overwrite each other's log entries.
    const newId = deps.newId || (() => crypto.randomUUID());
    const updateData: Record<string, any> = {
      status: action,
      rejectionNote: action === 'Rejected' ? note : '',
      reviewedBy: args.adminUid,
      reviewedByName: args.adminName,
      reviewedAt: args.now,
      reviewLogs: [
        ...(Array.isArray(fresh.reviewLogs) ? fresh.reviewLogs : []),
        {
          id: 'review_' + newId(),
          action,
          note: action === 'Rejected' ? note : '',
          by: args.adminUid,
          byName: args.adminName,
          at: args.now,
          // When this decision came from AI and the admin applied it,
          // the log says so — a human-read decision carries no marker.
          aiAssisted: args.aiProvenance ? true : undefined,
          aiModel: args.aiProvenance?.model || undefined,
        },
      ],
    };
    if (args.aiProvenance) {
      // Stamp AI provenance at APPLY time: which model decided, what it
      // decided, why, and which admin applied it.
      updateData.aiReview = {
        ...args.aiProvenance,
        appliedBy: args.adminUid,
        appliedByName: args.adminName,
        at: args.now,
      };
    } else {
      // A fresh human decision supersedes any earlier AI-applied one.
      updateData.aiReview = null;
    }
    // The applicant-facing verdict follows the decision (owner addition
    // #2) — including resolving a FurtherReviewRequested doc.
    const humanApplicantVerdict = applicantVerdictForHumanStatus(
      action,
      action === 'Rejected' ? note : undefined
    );
    if (humanApplicantVerdict) updateData.applicantVerdict = humanApplicantVerdict;

    try {
      await deps.updateDocument(applicationId, docId, updateData);
    } catch {
      return { outcome: 'failed', doc: fresh };
    }

    try {
      await deps.writeAuditLog(
        'REVIEW_DOCUMENT',
        applicationId,
        `Document ${docId} changed to ${action} by ${args.adminName}${note ? ` — ${note}` : ''}`
      );
    } catch {
      /* audit is best-effort — the decision stands */
    }

    try {
      await deps.notifyDecision({
        action,
        docName: String(fresh.name || docId),
        note,
      });
    } catch {
      /* notification is best-effort — the decision stands */
    }

    return { outcome: 'applied', updateData, doc: { ...fresh, ...updateData } };
  } finally {
    deps.inFlight.delete(flightKey);
    deps.onInFlightChange?.(docId, false);
  }
}

/**
 * Endpoint-surface twin of the layer-1 in-flight claim, for decision
 * surfaces whose write is composed SERVER-side rather than through
 * applyDocumentDecision — the Documents Centre modal is the user: its
 * human-decision / override writes go through the hardened centre
 * endpoints (PR #1236), so the recorded-decision layer lives on the
 * server, but the double-fire discipline is the same and shared from
 * here: the claim is taken SYNCHRONOUSLY at click time, before any
 * await, and a second invocation for the same document is refused
 * outright — regardless of which centre surface (list modal today,
 * any future one) mounted the button. One registry for the whole
 * endpoint surface family.
 */
const endpointDecisionFlights = new Set<string>();

/** Claim a document decision flight. Returns false when one is already in flight. */
export function tryClaimEndpointDocumentDecision(flightKey: string): boolean {
  if (endpointDecisionFlights.has(flightKey)) return false;
  endpointDecisionFlights.add(flightKey);
  return true;
}

/** Release a flight claimed with tryClaimEndpointDocumentDecision. */
export function releaseEndpointDocumentDecision(flightKey: string): void {
  endpointDecisionFlights.delete(flightKey);
}

/** Is an endpoint-surface decision for this key currently in flight? */
export function isEndpointDocumentDecisionInFlight(flightKey: string): boolean {
  return endpointDecisionFlights.has(flightKey);
}

/**
 * Server twin for POST /api/admin/documents/update-status: is this
 * request a REPLAY of a decision already recorded on the document?
 *
 * A replay must be provable — the endpoint suppresses the write (and
 * its push notification) only when the payload carries an identity that
 * matches the record: the latest reviewLog entry id the client already
 * submitted once, or the same AI decision (model + decision + reason +
 * provenance timestamp). Only decision targets (Approved/Rejected) at an
 * unchanged status qualify; anything else — a status change, an interim
 * validation re-apply, a fresh validation with new ids — writes as
 * before. When no identity is provable the answer is always false:
 * never suppress a write that cannot be proven to be a replay.
 */
export function isDuplicateDocumentStatusSubmission(
  docBefore: Record<string, any> | null | undefined,
  body: Record<string, any> | null | undefined
): boolean {
  const target = body?.newStatus;
  if (target !== 'Approved' && target !== 'Rejected') return false;
  if (!docBefore || docBefore.status !== target) return false;

  // Identity A: replayed reviewLogs — the payload's latest entry is the
  // very entry already recorded (client-generated ids are stable per
  // review result, e.g. the validation path's 'sys_…' entries).
  const payloadLogs: any[] = Array.isArray(body?.reviewLogs) ? body.reviewLogs : [];
  const storedLogs: any[] = Array.isArray(docBefore.reviewLogs) ? docBefore.reviewLogs : [];
  const pLast = payloadLogs[payloadLogs.length - 1];
  const sLast = storedLogs[storedLogs.length - 1];
  if (pLast?.id && sLast?.id && String(pLast.id) === String(sLast.id)) return true;

  // Identity B: the same AI decision re-applied — the stored aiReview
  // provenance matches the payload's on model, decision, reason and
  // its provenance timestamp (a fresh review stamps a new one).
  const pa = body?.aiReview;
  const sa = docBefore.aiReview;
  if (pa && sa && typeof pa === 'object' && typeof sa === 'object') {
    if (
      String(pa.model || '') === String(sa.model || '') &&
      String(pa.decision || '') === String(sa.decision || '') &&
      String(pa.reason || '') === String(sa.reason || '') &&
      pa.at && sa.at && String(pa.at) === String(sa.at)
    ) {
      return true;
    }
  }
  return false;
}
